import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export type HorizontalFilterPillOption<T> = { value: T | null; label: string };

export interface HorizontalFilterPillRowTheme {
  rowClass: string;
  labelIconShellClass: string;
  labelIconClass: string;
  labelTextClass: string;
  trackShellClass: string;
  pillActiveClass: string;
  pillInactiveClass: string;
  scrollBtnClass: string;
  /** Sliding active-chip fill (quiz-run style). */
  indicatorClass: string;
}

function valuesEqual<T>(a: T, b: T): boolean {
  return a === b;
}

const INDICATOR_TRANSITION =
  'left 0.35s cubic-bezier(0.4, 0, 0.2, 1), width 0.35s cubic-bezier(0.4, 0, 0.2, 1)';

export interface HorizontalFilterPillRowProps<T extends string | number> {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  options: HorizontalFilterPillOption<T>[];
  active: T | null;
  onChange: (value: T | null) => void;
  theme: HorizontalFilterPillRowTheme;
  /** Re-measure the sliding indicator after a parent expand animation. */
  remeasureKey?: string | number | boolean;
  /**
   * When true, label + track become siblings of a parent CSS grid so chip tracks
   * share one start edge across rows.
   */
  alignTracks?: boolean;
  /** Hide the icon+label row and keep the label for assistive text only. */
  hideLabel?: boolean;
  /**
   * When true and `active` is not in `options`, no chip is selected and the
   * sliding indicator is hidden (instead of falling back to the first chip).
   */
  allowUnmatched?: boolean;
}

export function HorizontalFilterPillRow<T extends string | number>({
  label,
  icon: Icon,
  options,
  active,
  onChange,
  theme,
  remeasureKey,
  alignTracks = false,
  hideLabel = false,
  allowUnmatched = false
}: HorizontalFilterPillRowProps<T>) {
  const labelId = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const pillContainerRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [scrollHints, setScrollHints] = useState({ canLeft: false, canRight: false });
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  const activeIndex = useMemo(() => {
    const idx = options.findIndex((opt) => valuesEqual(opt.value, active));
    if (idx >= 0) return idx;
    return allowUnmatched ? -1 : 0;
  }, [options, active, allowUnmatched]);

  const updateIndicator = useCallback(() => {
    const container = pillContainerRef.current;
    if (!container) return;
    if (activeIndex < 0) {
      setIndicatorStyle({ left: 0, width: 0 });
      return;
    }
    const chip = chipRefs.current[activeIndex];
    if (!chip) return;
    const cRect = container.getBoundingClientRect();
    const aRect = chip.getBoundingClientRect();
    setIndicatorStyle({
      left: aRect.left - cRect.left,
      width: aRect.width
    });
  }, [activeIndex]);

  const updateScrollHints = useCallback(() => {
    const port = scrollRef.current;
    const inner = pillContainerRef.current;
    if (!port || !inner) {
      setScrollHints({ canLeft: false, canRight: false });
      return;
    }
    const portRect = port.getBoundingClientRect();
    const innerRect = inner.getBoundingClientRect();
    const tolerance = 2;
    setScrollHints({
      canLeft: innerRect.left < portRect.left - tolerance,
      canRight: innerRect.right > portRect.right + tolerance
    });
  }, []);

  const scroll = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(200, el.clientWidth * 0.75), behavior: 'smooth' });
    const refresh = () => {
      updateScrollHints();
      updateIndicator();
    };
    if ('onscrollend' in el) {
      el.addEventListener('scrollend', refresh, { once: true });
    } else {
      window.setTimeout(refresh, 350);
    }
  };

  const centerActiveChip = useCallback(() => {
    const el = scrollRef.current;
    const chip = chipRefs.current[activeIndex];
    if (!el || !chip) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) return;
    const elRect = el.getBoundingClientRect();
    const chipRect = chip.getBoundingClientRect();
    const delta = chipRect.left + chipRect.width / 2 - (elRect.left + elRect.width / 2);
    if (Math.abs(delta) < 2) return;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  }, [activeIndex]);

  useLayoutEffect(() => {
    updateIndicator();
    centerActiveChip();
  }, [centerActiveChip, updateIndicator]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      updateIndicator();
      updateScrollHints();
    }, 380);
    return () => window.clearTimeout(timer);
  }, [updateIndicator, updateScrollHints, remeasureKey]);

  useEffect(() => {
    const el = scrollRef.current;
    const track = pillContainerRef.current;
    if (!el) return;

    const onScroll = () => {
      requestAnimationFrame(() => {
        updateScrollHints();
        updateIndicator();
      });
    };
    updateScrollHints();
    updateIndicator();
    el.addEventListener('scroll', onScroll, { passive: true });

    const ro = new ResizeObserver(() => {
      updateScrollHints();
      updateIndicator();
    });
    ro.observe(el);
    if (track) ro.observe(track);
    Array.from(el.children).forEach((child) => ro.observe(child));
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [options.length, updateScrollHints, updateIndicator]);

  if (options.length === 0) {
    return null;
  }

  const scrollBtnClass = `absolute top-1/2 z-20 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border shadow-sm backdrop-blur-sm transition pointer-events-auto ${theme.scrollBtnClass}`;

  const labelBlock = hideLabel ? (
    <span id={labelId} className="sr-only">
      {label}
    </span>
  ) : (
    <div id={labelId} className="flex shrink-0 items-center gap-1 sm:gap-1.5">
      <span className={theme.labelIconShellClass}>
        <Icon className={`h-3.5 w-3.5 ${theme.labelIconClass}`} aria-hidden />
      </span>
      <span className={theme.labelTextClass}>{label}</span>
    </div>
  );

  const trackBlock = (
    <div className={`relative min-w-0 w-full ${alignTracks ? '' : 'flex-1'} ${theme.trackShellClass}`}>
      {scrollHints.canLeft && (
        <button
          type="button"
          onClick={() => scroll(-1)}
          className={`left-0.5 ${scrollBtnClass}`}
          aria-label={`Scroll ${label} filters left`}
        >
          <ChevronLeft className="h-3.5 w-3.5 drop-shadow-sm" aria-hidden />
        </button>
      )}
      {scrollHints.canRight && (
        <button
          type="button"
          onClick={() => scroll(1)}
          className={`right-0.5 ${scrollBtnClass}`}
          aria-label={`Scroll ${label} filters right`}
        >
          <ChevronRight className="h-3.5 w-3.5 drop-shadow-sm" aria-hidden />
        </button>
      )}
      <div
        ref={scrollRef}
        className="horizontal-filter-pill-strip overflow-x-auto overflow-y-hidden scroll-smooth overscroll-x-contain [-webkit-overflow-scrolling:touch]"
        style={{ touchAction: 'pan-x pan-y' }}
      >
        <div
          ref={pillContainerRef}
          role="group"
          aria-labelledby={labelId}
          className="relative flex w-max items-center gap-0.5 p-0.5 sm:gap-1"
        >
          <div
            className={`pointer-events-none absolute top-0.5 bottom-0.5 rounded-full ${theme.indicatorClass}`}
            style={{
              left: indicatorStyle.left,
              width: indicatorStyle.width,
              transition: indicatorStyle.width > 0 ? INDICATOR_TRANSITION : 'none'
            }}
          />
          {options.map((opt, index) => {
            const isActive = valuesEqual(opt.value, active);
            return (
              <button
                key={opt.label}
                ref={(el) => {
                  chipRefs.current[index] = el;
                }}
                type="button"
                onClick={() => onChange(opt.value)}
                aria-pressed={isActive}
                title={opt.label}
                className={`relative z-10 inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold leading-none transition-colors duration-300 sm:px-3 sm:text-xs ${
                  isActive ? theme.pillActiveClass : theme.pillInactiveClass
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (hideLabel) {
    return (
      <div className={`min-w-0 w-max max-w-full ${theme.rowClass}`}>
        {labelBlock}
        {trackBlock}
      </div>
    );
  }

  return (
    <div className={alignTracks ? 'contents' : `flex min-w-0 items-center gap-1.5 sm:gap-2 ${theme.rowClass}`}>
      {labelBlock}
      {trackBlock}
    </div>
  );
}
